// src/app/services/notifications.service.ts
import { Injectable, OnDestroy } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { BehaviorSubject, Observable, Subscription } from "rxjs";
import { Router } from "@angular/router";
import { tap } from "rxjs/operators";
import { environment } from "environments/environment";
import { Notifications, NotificationType } from "app/services/models/notification.model";
import { User } from "app/services/models/user.model";
import { CurrentService } from "app/services/current.service";

@Injectable({
  providedIn: "root",
})
export class NotificationsService implements OnDestroy {
  private subscriptions: Subscription[] = [];
  private apiUrl = `${environment.apiUrl}/api/notifications`;
  private currentUserNotifications = new BehaviorSubject<Notifications[]>([]);
  private unreadCount = new BehaviorSubject<number>(0);
  user: User;

  constructor(
    private http: HttpClient,
    private router: Router,
    private currentService: CurrentService
  ) {
    this.subscriptions.push(
      this.currentService.currentUser.subscribe((user) => {
        this.user = user;
      })
    );

    // this.loadCurrentUserNotifications();
  }

  // Observable pour les components
  public currentNotifications$ = this.currentUserNotifications.asObservable();
  public unreadCount$ = this.unreadCount.asObservable();

  // Charger les notifications de l'utilisateur courant
  loadCurrentUserNotifications(): void {
    if (this.user?.id) {
      this.http
        .get<Notifications[]>(`${this.apiUrl}/user/${this.user.id}`)
        .pipe(
          tap((notifications) => {
            this.calculateUnreadCount(notifications);
          })
        )
        .subscribe((notifications) => {
          this.currentUserNotifications.next(notifications);
        });
    }
  }

  // Récupérer toutes les notifications d'un utilisateur
  getUserNotifications(userId: string, includeRead: boolean = false): Observable<Notifications[]> {
    return this.http.get<Notifications[]>(
      `${this.apiUrl}/user/${userId}?includeRead=${includeRead}`
    );
  }

  // Marquer une notification comme lue
  markAsRead(notificationId: string): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/mark-read/${notificationId}`, {}).pipe(
      tap(() => {
        // Mettre à jour l'état local
        const currentNotifications = this.currentUserNotifications.getValue();
        const updatedNotifications = currentNotifications.map((n) => {
          if (n.id === notificationId) {
            return { ...n, isRead: true };
          }
          return n;
        });
        // il faut retirer la notification de la liste
        const filteredNotifications = updatedNotifications.filter((n) => n.id !== notificationId);
        this.currentUserNotifications.next(filteredNotifications);
        this.calculateUnreadCount(filteredNotifications);
      })
    );
  }

  // Marquer toutes les notifications comme lues
  markAllAsRead(userId: string): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/mark-all-read/${userId}`, {}).pipe(
      tap(() => {
        // Vider la liste des notifications en front
        this.currentUserNotifications.next([]);
        this.unreadCount.next(0);
      })
    );
  }

  // Récupérer le nombre exact de notifications non lues depuis l'API
  getUnreadNotificationCount(userId: string): Observable<{ unreadCount: number }> {
    return this.http.get<{ unreadCount: number }>(`${this.apiUrl}/user/${userId}/unread-count`);
  }

  // Charger et mettre à jour le compteur depuis l'API
  loadUnreadCount(): void {
    if (this.user?.id) {
      this.getUnreadNotificationCount(this.user.id).subscribe((response) => {
        this.unreadCount.next(response.unreadCount);
      });
    }
  }

  // Calculer le nombre de notifications non lues
  private calculateUnreadCount(notifications: Notifications[]): void {
    const count = notifications.filter((n) => !n.isRead).length;
    this.unreadCount.next(count);
  }

  // Mettre à jour le compteur manuellement (utilisé par GraphQL)
  updateUnreadCount(count: number): void {
    this.unreadCount.next(count);
  }

  /**
   * Transforme une ActionUrl générique de l'API en route Angular spécifique
   * Exemples:
   *   /trips/{id} → utrav/trips/view/{id}
   *   /bookings/{id} → utrav/booking/view/{id}
   *   /payments/{id} → utrav/account/history?paymentId={id}
   *   /loyalty/transactions/{id} → utrav/loyalty?transactionId={id}
   */
  private transformActionUrlToAngularRoute(actionUrl: string): string {
    if (!actionUrl || actionUrl.trim() === "") {
      return "";
    }

    // Retirer le slash initial si présent
    const cleanUrl = actionUrl.startsWith("/") ? actionUrl.substring(1) : actionUrl;

    // Mapping des patterns API → Angular routes
    const patterns = [
      { regex: /^trips\/([a-f0-9-]+)$/i, route: (id: string) => `utrav/trips/view/${id}` },
      { regex: /^bookings\/([a-f0-9-]+)$/i, route: (id: string) => `utrav/booking/view/${id}` },
      {
        regex: /^payments?\/([a-f0-9-]+)$/i, // Support /payment/ et /payments/
        route: (id: string) => `utrav/account/history?paymentId=${id}`,
      },
      {
        regex: /^loyalty\/transactions\/([a-f0-9-]+)$/i,
        route: (id: string) => `utrav/loyalty?transactionId=${id}`,
      },
      { regex: /^loyalty\/dashboard$/i, route: () => `utrav/loyalty` },
    ];

    for (const pattern of patterns) {
      const match = cleanUrl.match(pattern.regex);
      if (match) {
        const transformedRoute = pattern.route(match[1]);
        console.log(`🔄 ActionUrl transformée: ${actionUrl} → ${transformedRoute}`);
        return transformedRoute;
      }
    }

    // Si pas de match, on préfixe juste avec /utrav
    console.log(`⚠️ Pattern non reconnu, ajout du préfixe /utrav: ${actionUrl}`);
    return `utrav/${cleanUrl}`;
  }

  // Naviguer vers l'entité associée à la notification avec gestion intelligente de l'ActionUrl
  navigateToNotificationTarget(notification: Notifications): void {
    this.markAsRead(notification.id).subscribe(() => {
      console.log("🔔 Navigation depuis notification:", notification.title);
      console.log("📍 ActionUrl (API):", notification.actionUrl);
      console.log("📦 Type:", notification.type);

      // ✅ STRATÉGIE 1 : Si ActionUrl existe, la transformer en route Angular
      if (notification.actionUrl && notification.actionUrl.trim() !== "") {
        const angularRoute = this.transformActionUrlToAngularRoute(notification.actionUrl);
        console.log("✅ Route Angular:", angularRoute);

        // Si l'URL contient des query params (ex: ?paymentId=xxx)
        if (angularRoute.includes("?")) {
          const [path, queryString] = angularRoute.split("?");
          const queryParams: any = {};
          queryString.split("&").forEach((param) => {
            const [key, value] = param.split("=");
            queryParams[key] = value;
          });
          this.router.navigate([`/${path}`], { queryParams });
        } else {
          this.router.navigate([`/${angularRoute}`]);
        }
        return;
      }

      // ✅ STRATÉGIE 2 : Sinon, navigation par type (fallback)
      console.log("⚠️ Pas d'ActionUrl, navigation par type");
      const normalizedType = this.normalizeNotificationType(notification.type);

      switch (normalizedType) {
        case NotificationType.BOOKING:
          if (notification.bookingId) {
            this.router.navigate(["/utrav/booking/view", notification.bookingId]);
          } else {
            this.router.navigate(["/utrav/booking"]);
          }
          break;
        case NotificationType.TRIP:
          if (notification.tripId) {
            this.router.navigate(["/utrav/trips/view", notification.tripId]);
          } else {
            this.router.navigate(["/utrav/trips"]);
          }
          break;
        case NotificationType.PAYMENT:
          if (notification.paymentId) {
            this.router.navigate(["/utrav/account/history"], {
              queryParams: { paymentId: notification.paymentId },
            });
          } else {
            this.router.navigate(["/utrav/payment"]);
          }
          break;
        case NotificationType.APPOINTMENT:
          this.router.navigate(["/utrav/appointment"]);
          break;
        case NotificationType.LOYALTY:
          if ((notification as any).loyaltyTransactionId) {
            this.router.navigate(["/utrav/loyalty"], {
              queryParams: { transactionId: (notification as any).loyaltyTransactionId },
            });
          } else {
            this.router.navigate(["/utrav/loyalty"]);
          }
          break;
        case NotificationType.SYSTEM:
          // Notification système : pas de redirection, juste mark as read
          console.log("ℹ️ Notification système - pas de redirection");
          break;
        default:
          console.log("⚠️ Type non géré, redirection vers dashboard");
          this.router.navigate(["/utrav/dashboard"]);
          break;
      }
    });
  }

  // Normaliser les types de notifications (snake_case → PascalCase)
  private normalizeNotificationType(type: string): NotificationType {
    const lowerType = type.toLowerCase();

    // Mapper les types snake_case vers l'enum
    if (lowerType.includes("booking")) return NotificationType.BOOKING;
    if (lowerType.includes("payment")) return NotificationType.PAYMENT;
    if (lowerType.includes("trip")) return NotificationType.TRIP;
    if (lowerType.includes("loyalty") || lowerType.includes("points"))
      return NotificationType.LOYALTY;
    if (lowerType.includes("appointment")) return NotificationType.APPOINTMENT;

    // Sinon, capitaliser la première lettre
    return (type.charAt(0).toUpperCase() + type.slice(1)) as NotificationType;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.subscriptions = [];
  }
}
